import { useState, useEffect, useCallback } from "react";
import { X, FlaskConical, Save, LogOut, Beaker, Plus, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { usePerformanceLab, LabPlayer, LabFixture } from "@/contexts/PerformanceLabContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useOrganization } from "@/contexts/OrganizationContext";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { LabSessionTimer } from "./LabSessionTimer";
import { LabReadinessGate, ReadinessCheck } from "./LabReadinessGate";
import { LabSquadPanel } from "./LabSquadPanel";
import { LabFixturePanel } from "./LabFixturePanel";
import { LabSaveModal } from "./LabSaveModal";
import { LabPIUpgradeCard, LabAIComingSoonCard } from "./LabPIUpgradeCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { ROUTES } from '@/lib/routes';

const SIX_WEEKS_MS = 6 * 7 * 24 * 60 * 60 * 1000;

export function PerformanceLabModal() {
  const { translate } = useTranslation();
  const { session, openLab, discardAndExit, loadSnapshot, addScenario, setActiveScenario } = usePerformanceLab();
  const { effectiveTier, isFeatureEnabled } = useSubscription();
  const { currentOrganization } = useOrganization();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [readinessChecks, setReadinessChecks] = useState<ReadinessCheck[] | null>(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [addScenarioMode, setAddScenarioMode] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState("");

  const hasLab = isFeatureEnabled("performance_lab");
  const hasMultiScenario = isFeatureEnabled("lab_multi_scenario");
  const hasLoadSim = isFeatureEnabled("lab_load_simulation");

  // Load snapshot when modal opens
  useEffect(() => {
    if (!session.isOpen) return;
    if (session.players.length > 0) return; // already loaded
    loadData();
  }, [session.isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async () => {
    const orgId = currentOrganization?.id;
    if (!orgId) return;
    setLoading(true);
    setLoadError(false);

    try {
      // Fetch players
      const { data: playersData, error: playersError } = await supabase
        .from("players")
        .select("id, first_name, last_name, position, status")
        .eq("organization_id", orgId)
        .limit(200);

      if (playersError) throw playersError;

      // Fetch RTP cases for injured players to get current stage
      const { data: rtpData } = await supabase
        .from("rtp_cases")
        .select("athlete_id, status")
        .eq("organization_id", orgId)
        .in("status", ["stage_0", "stage_1", "stage_2", "stage_3", "stage_4", "stage_5"]);

      const rtpStageByPlayer: Record<string, string> = {};
      (rtpData ?? []).forEach((r: any) => {
        rtpStageByPlayer[r.athlete_id] = r.status;
      });

      // Fetch upcoming fixtures (next 6 weeks)
      const now = new Date().toISOString().split("T")[0];
      const sixWeeksLater = new Date(Date.now() + SIX_WEEKS_MS).toISOString().split("T")[0];
      const { data: matchesData, error: matchesError } = await supabase
        .from("matches")
        .select("id, match_date, opponent, venue, competition")
        .eq("organization_id", orgId)
        .gte("match_date", now)
        .lte("match_date", sixWeeksLater)
        .order("match_date", { ascending: true })
        .limit(15);

      if (matchesError) throw matchesError;

      // Check wellness check-ins (last 4 weeks) — query by player_ids for accuracy
      const playerIds = (playersData ?? []).map((p: any) => p.id);
      let wellnessCount = 0;
      if (playerIds.length > 0) {
        const fourWeeksAgo = new Date(Date.now() - 4 * 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        const { count } = await supabase
          .from("wellness_daily")
          .select("id", { count: "exact", head: true })
          .in("player_id", playerIds.slice(0, 50)) // limit to avoid query size issues
          .gte("wellness_date", fourWeeksAgo);
        wellnessCount = count ?? 0;
      }

      const players: LabPlayer[] = (playersData ?? []).map((p: any) => ({
        id: p.id,
        first_name: p.first_name,
        last_name: p.last_name,
        position: p.position,
        status: (p.status as LabPlayer["status"]) ?? "fit",
        rtp_stage: rtpStageByPlayer[p.id] ?? null,
      }));

      const fixtures: LabFixture[] = (matchesData ?? []).map((m: any) => ({
        id: m.id,
        match_date: m.match_date,
        opponent: m.opponent,
        venue: m.venue,
        competition: m.competition,
      }));

      // Build readiness checks
      const playerCount = players.length;
      const hasFixtures = fixtures.length > 0;
      const hasWellness = wellnessCount >= 3;
      // RTP check: passes if no injured player lacks an RTP case
      const injuredPlayerIds = players.filter(p => p.status === "injured").map(p => p.id);
      const rtpCurrent = injuredPlayerIds.length === 0 ||
        injuredPlayerIds.every(id => rtpStageByPlayer[id] !== undefined);

      const checks: ReadinessCheck[] = [
        {
          key: "players",
          labelKey: "lab_readiness_check_players",
          met: playerCount >= 16,
          linkPath: ROUTES.PLAYERS,
          linkLabelKey: "lab_readiness_link_players",
        },
        {
          key: "fixtures",
          labelKey: "lab_readiness_check_fixtures",
          met: hasFixtures,
          linkPath: ROUTES.MATCHES,
          linkLabelKey: "lab_readiness_link_fixtures",
        },
        {
          key: "rtp",
          labelKey: "lab_readiness_check_rtp",
          met: rtpCurrent,
          linkPath: ROUTES.RTP,
          linkLabelKey: "lab_readiness_link_rtp",
        },
        {
          key: "wellness",
          labelKey: "lab_readiness_check_wellness",
          met: hasWellness,
          linkPath: ROUTES.WELLNESS,
          linkLabelKey: "lab_readiness_link_wellness",
        },
      ];

      const allMet = checks.every((c) => c.met);
      setReadinessChecks(allMet ? null : checks); // null = skip readiness gate

      if (allMet) {
        loadSnapshot(players, fixtures);
      }

      analytics.track("lab.opened", {
        tier: effectiveTier,
        readiness_met: allMet,
      });
    } catch (err) {
      console.error("Performance Lab snapshot load failed:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, effectiveTier, loadSnapshot]);

  const handleLaunchFromGate = async () => {
    const orgId = currentOrganization?.id;
    if (!orgId) return;

    const { data: playersData } = await supabase
      .from("players")
      .select("id, first_name, last_name, position, status")
      .eq("organization_id", orgId)
      .limit(200);

    const { data: rtpData } = await supabase
      .from("rtp_cases")
      .select("athlete_id, status")
      .eq("organization_id", orgId)
      .in("status", ["stage_0", "stage_1", "stage_2", "stage_3", "stage_4", "stage_5"]);

    const rtpStageByPlayer: Record<string, string> = {};
    (rtpData ?? []).forEach((r: any) => {
      rtpStageByPlayer[r.athlete_id] = r.status;
    });

    const now = new Date().toISOString().split("T")[0];
    const sixWeeksLater = new Date(Date.now() + SIX_WEEKS_MS).toISOString().split("T")[0];
    const { data: matchesData } = await supabase
      .from("matches")
      .select("id, match_date, opponent, venue, competition")
      .eq("organization_id", orgId)
      .gte("match_date", now)
      .lte("match_date", sixWeeksLater)
      .order("match_date", { ascending: true })
      .limit(15);

    const players: LabPlayer[] = (playersData ?? []).map((p: any) => ({
      id: p.id,
      first_name: p.first_name,
      last_name: p.last_name,
      position: p.position,
      status: (p.status as LabPlayer["status"]) ?? "fit",
      rtp_stage: rtpStageByPlayer[p.id] ?? null,
    }));

    const fixtures: LabFixture[] = (matchesData ?? []).map((m: any) => ({
      id: m.id,
      match_date: m.match_date,
      opponent: m.opponent,
      venue: m.venue,
      competition: m.competition,
    }));

    loadSnapshot(players, fixtures);
    setReadinessChecks(null);
  };

  const handleDiscardClick = () => {
    if (session.isDirty) {
      setDiscardDialogOpen(true);
    } else {
      discardAndExit();
    }
  };

  const handleConfirmDiscard = () => {
    analytics.track("lab.discarded");
    discardAndExit();
    setDiscardDialogOpen(false);
  };

  const handleAddScenario = () => {
    if (!newScenarioName.trim()) return;
    addScenario(newScenarioName.trim());
    setNewScenarioName("");
    setAddScenarioMode(false);
  };

  if (!session.isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex flex-col">

        {/* Session Banner */}
        <div className="shrink-0 flex items-center justify-between px-4 py-2.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {translate("lab_banner_text")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {session.sessionStart && (
              <LabSessionTimer startTime={session.sessionStart} />
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
              onClick={() => setSaveModalOpen(true)}
            >
              <Save className="h-3.5 w-3.5" />
              {translate("lab_banner_save")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1.5 text-xs text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
              onClick={handleDiscardClick}
            >
              <LogOut className="h-3.5 w-3.5" />
              {translate("lab_banner_discard")}
            </Button>
          </div>
        </div>

        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b bg-card">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Beaker className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-base leading-tight">
                {translate("lab_title")}
              </h2>
              <p className="text-xs text-muted-foreground">{translate("lab_subtitle")}</p>
            </div>
          </div>

          {/* Multi-scenario tabs (PI only) */}
          {hasMultiScenario && (
            <div className="flex items-center gap-1">
              {session.scenarios.map((name, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveScenario(idx)}
                  className={cn(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors",
                    session.activeScenario === idx
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {name}
                </button>
              ))}
              {session.scenarios.length < 3 && !addScenarioMode && (
                <button
                  onClick={() => setAddScenarioMode(true)}
                  className="rounded-md p-1 hover:bg-muted text-muted-foreground"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
              {addScenarioMode && (
                <div className="flex items-center gap-1">
                  <Input
                    value={newScenarioName}
                    onChange={(e) => setNewScenarioName(e.target.value)}
                    placeholder={translate("lab_scenario_name_placeholder")}
                    className="h-7 w-32 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddScenario();
                      if (e.key === "Escape") setAddScenarioMode(false);
                    }}
                    autoFocus
                  />
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleAddScenario}>
                    {translate("lab_scenario_add_btn")}
                  </Button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleDiscardClick}
            className="rounded-full p-1.5 hover:bg-muted transition-colors text-muted-foreground"
            aria-label={translate("lab_close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-2">
                <FlaskConical className="h-8 w-8 text-muted-foreground mx-auto animate-pulse" />
                <p className="text-sm text-muted-foreground">{translate("lab_loading")}</p>
              </div>
            </div>
          ) : loadError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-3 max-w-sm">
                <p className="text-sm text-destructive font-medium">{translate("lab_loading_error")}</p>
                <Button size="sm" variant="outline" onClick={() => loadData()}>
                  {translate("lab_retry")}
                </Button>
              </div>
            </div>
          ) : readinessChecks ? (
            <LabReadinessGate checks={readinessChecks} onLaunch={handleLaunchFromGate} />
          ) : (
            <div className="flex h-full">
              {/* Left: Squad State */}
              <div className="w-72 shrink-0 border-r flex flex-col overflow-hidden">
                <LabSquadPanel />
              </div>

              {/* Right: Fixture & Lineup */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <LabFixturePanel />
              </div>

              {/* PI features sidebar (only shown to Performance Core to entice upgrade) */}
              {!hasMultiScenario && (
                <div className="w-64 shrink-0 border-l p-4 overflow-y-auto space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {translate("lab_pi_features_heading")}
                    </p>
                  </div>
                  <LabPIUpgradeCard
                    featureKey="lab_pi_multi_scenario_title"
                    descKey="lab_pi_multi_scenario_desc"
                  />
                  <LabPIUpgradeCard
                    featureKey="lab_pi_load_sim_title"
                    descKey="lab_pi_load_sim_desc"
                  />
                  <LabAIComingSoonCard />
                </div>
              )}

              {/* PI-tier: load sim panel */}
              {hasLoadSim && (
                <div className="w-64 shrink-0 border-l p-4 overflow-y-auto space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {translate("lab_pi_load_sim_title")}
                  </p>
                  {session.players.filter(p => p.status === "fit").slice(0, 8).map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-xs">
                      <div className="flex-1 truncate">{p.first_name} {p.last_name}</div>
                      <div className="flex gap-0.5">
                        {Array.from({ length: session.fixtures.length > 0 ? Math.min(session.fixtures.length, 6) : 3 }).map((_, i) => (
                          <div
                            key={i}
                            className={cn(
                              "h-3 w-3 rounded-sm",
                              i < 2 ? "bg-emerald-400" : i < 4 ? "bg-amber-400" : "bg-red-400",
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground">{translate("lab_load_sim_legend")}</p>
                  <LabAIComingSoonCard />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Save Modal */}
      <LabSaveModal open={saveModalOpen} onOpenChange={setSaveModalOpen} />

      {/* Discard confirmation */}
      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate("lab_discard_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translate("lab_discard_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{translate("lab_discard_go_back")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDiscard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {translate("lab_discard_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
